import type { ApolloCache, ApolloClient, Reference } from '@apollo/client'

import { NotesQuery, notesInput } from './notesQueries'
import type { NoteSubject } from './noteSubject'
import type {
  CollaborationNote,
  NoteReaction,
  NoteReactionAccount,
} from './notesQueries'

export type CreatedNoteEdge = {
  __typename?: 'CollaborationNoteEdge'
  cursor: string
  node: CollaborationNote & { __typename?: 'CollaborationNote' }
}

/** A Post's Note counts; `null` means not currently known. */
export type NotesSummary = {
  __typename?: 'CollaborationNotesSummary'
  unreadNoteCount?: number | null
  unreadMentionCount?: number | null
  /** Total Notes on the Post; `null` when a create or delete made it unknown. */
  noteCount?: number | null
}

/**
 * A caller that knows the counts the server reported writes them. A caller
 * that only knows the counts changed marks them unavailable instead of
 * guessing: the next Post load brings the real values.
 */
const writeNotesSummary = (
  cache: ApolloCache,
  postId: string,
  summary: NotesSummary,
): void => {
  cache.modify({
    id: cache.identify({ __typename: 'Post', id: postId }),
    fields: {
      collaborationNotesSummary(
        existing: NotesSummary | Reference | null,
        { isReference },
      ) {
        if (!existing || isReference(existing)) return existing
        return { ...existing, ...summary }
      },
    },
  })
}

/** Which cached counts a change without server counts leaves unknown. */
type NotesChange = 'membership' | 'content'

const invalidateSummary = (
  cache: ApolloCache,
  postId: string,
  change: NotesChange,
): void => {
  cache.modify({
    id: cache.identify({ __typename: 'Post', id: postId }),
    fields: {
      collaborationNotesSummary(
        existing: NotesSummary | Reference | null,
        { isReference },
      ) {
        if (!existing || isReference(existing)) return existing
        // An edit can change what is unread, never how many Notes exist.
        const unread = {
          ...existing,
          unreadNoteCount: null,
          unreadMentionCount: null,
        }
        return change === 'membership' ? { ...unread, noteCount: null } : unread
      },
    },
  })
}

/**
 * Unread counts are cached on Posts only. A draft's thread reloads its
 * `notesSummary` from the ContentItem's own stream instead.
 */
const syncPostSummary = (
  cache: ApolloCache,
  subject: NoteSubject,
  summary: NotesSummary | undefined,
  change: NotesChange,
): void => {
  if (subject.subjectType !== 'post') return
  if (summary) writeNotesSummary(cache, subject.subjectId, summary)
  else invalidateSummary(cache, subject.subjectId, change)
}

/**
 * Reads back the edge of a Note another account created, as announced by a
 * live event. The shared event carries no cursor and no allowed actions for
 * this reader, so the edge comes from the thread's first page. Resolves
 * `undefined` when the read fails or the Note is not on the first page. The
 * account's own creates need no read: the create response returns the edge.
 */
export async function fetchCreatedNote(
  client: ApolloClient,
  subject: NoteSubject,
  noteId: string,
): Promise<CreatedNoteEdge | undefined> {
  const response = await client.query({
    query: NotesQuery,
    variables: { input: notesInput(subject), first: 10, after: null },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })
  if (response.error) return undefined
  return response.data?.collaborationNotes.edges.find(
    ({ node }) => node.id === noteId,
  )
}

export function insertCreatedNote(
  cache: ApolloCache,
  subject: NoteSubject,
  edge: CreatedNoteEdge,
  summary?: NotesSummary,
): void {
  cache.updateQuery(
    {
      query: NotesQuery,
      variables: { input: notesInput(subject), first: 10, after: null },
    },
    (data) => {
      if (!data?.collaborationNotes) return data
      const existingEdges = data.collaborationNotes.edges.filter(
        ({ node }) => node.id !== edge.node.id,
      )
      const edges = [edge, ...existingEdges]
      return {
        ...data,
        collaborationNotes: {
          ...data.collaborationNotes,
          edges,
          pageInfo: {
            ...data.collaborationNotes.pageInfo,
            startCursor: edge.cursor,
            endCursor: existingEdges.at(-1)?.cursor ?? edge.cursor,
          },
        },
      }
    },
  )
  syncPostSummary(cache, subject, summary, 'membership')
}

/**
 * Replaces one Note in its feed and nothing else. The subscription uses it
 * for `CollaborationNoteUpdated`, which fires for reactions as well as
 * edits: a reaction never changes a count, so the cached summary stays.
 */
export function replaceNoteInFeed(
  cache: ApolloCache,
  subject: NoteSubject,
  note: CollaborationNote,
): void {
  cache.updateQuery(
    {
      query: NotesQuery,
      variables: { input: notesInput(subject), first: 10, after: null },
    },
    (data) => {
      if (!data?.collaborationNotes) return data
      return {
        ...data,
        collaborationNotes: {
          ...data.collaborationNotes,
          edges: data.collaborationNotes.edges.map((edge) =>
            edge.node.id === note.id
              ? { ...edge, node: { ...edge.node, ...note } }
              : edge,
          ),
        },
      }
    },
  )
}

export function replaceUpdatedNote(
  cache: ApolloCache,
  subject: NoteSubject,
  note: CollaborationNote,
  summary?: NotesSummary,
): void {
  replaceNoteInFeed(cache, subject, note)
  syncPostSummary(cache, subject, summary, 'content')
}

export function removeDeletedNote(
  cache: ApolloCache,
  subject: NoteSubject,
  noteId: string,
  summary?: NotesSummary,
): void {
  cache.updateQuery(
    {
      query: NotesQuery,
      variables: { input: notesInput(subject), first: 10, after: null },
    },
    (data) => {
      if (!data?.collaborationNotes) return data
      return {
        ...data,
        collaborationNotes: {
          ...data.collaborationNotes,
          edges: data.collaborationNotes.edges.filter(
            ({ node }) => node.id !== noteId,
          ),
        },
      }
    },
  )
  syncPostSummary(cache, subject, summary, 'membership')
}

/**
 * The reactions with the account's reaction to one emoji set or cleared. It
 * mirrors the server's grouping so the change can render before the server
 * confirms it: a new emoji group goes last, and a group left with no accounts
 * disappears. Setting a state the reactions already hold changes nothing, so a
 * pending change can be laid over a server result that already includes it.
 */
export function setNoteReaction(
  reactions: readonly NoteReaction[],
  emoji: string,
  account: NoteReactionAccount,
  reacted: boolean,
): NoteReaction[] {
  const existing = reactions.find((reaction) => reaction.emoji === emoji)
  if (!existing) {
    return reacted
      ? [
          ...reactions,
          { emoji, count: 1, hasReacted: true, accounts: [account] },
        ]
      : [...reactions]
  }
  return reactions.flatMap((reaction) => {
    if (reaction.emoji !== emoji || reaction.hasReacted === reacted) {
      return [reaction]
    }
    const others = reaction.accounts.filter(({ id }) => id !== account.id)
    const accounts = reacted ? [...others, account] : others
    return accounts.length
      ? [
          {
            ...reaction,
            accounts,
            count: accounts.length,
            hasReacted: reacted,
          },
        ]
      : []
  })
}

/** Whether the current account holds a reaction with this emoji. */
export function hasReactedWith(
  reactions: readonly NoteReaction[],
  emoji: string,
): boolean {
  return reactions.some(
    (reaction) => reaction.emoji === emoji && reaction.hasReacted,
  )
}
